import { Storage } from 'aws-amplify'
import moment, { Duration } from 'moment'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import QRCode from 'qrcode'
import { CognitoIdentityServiceProvider, Config } from 'aws-sdk'
import json from '../localConfig.json'
import authConfig from '../config'

const userPoolId = authConfig.Auth['userPoolId']

const config = new Config({
    accessKeyId: json['accessKeyId'], secretAccessKey: json['secretAccessKey'], region: 'eu-west-1'
})

const cognito = new CognitoIdentityServiceProvider(config)

export const adminResetUserPassword = async (id: string, password: string) => {
    cognito.adminSetUserPassword({
        UserPoolId: userPoolId,
        Username: id,
        Password: password
    }).promise()    
}

export const handleAuthErrorCognitoMessages = (error: string) => {
    switch (error) {
        case 'UserNotFoundException':
          return 'Bruger findes ikke Forkert.';
        case 'NotAuthorizedException':
          return 'Forkert brugernavn eller kode.';
        case 'PasswordResetRequiredException':
          return 'Brugerens adgangskode skal nulstilles, inden de kan logge ind.';
        case 'UserNotConfirmedException':
          return 'Brugeren har endnu ikke bekræftet deres konto.';
        case 'CodeMismatchException':
          return 'Den angivne verifikationskode til flerfaktor-autentificering (MFA) er forkert.';
        case 'ExpiredCodeException':
          return 'Den angivne verifikationskode til MFA er udløbet.';
        case 'TooManyFailedAttemptsException':
          return 'Brugeren har overskredet det maksimale antal mislykkede loginforsøg tilladt.';
        case 'InvalidPasswordException':
          return 'Den angivne adgangskode opfylder ikke adgangskodepolitikken.';
        case 'InvalidParameterException':
          return 'En eller flere af de angivne parametre i autentificeringsanmodningen er ugyldige.';
        case 'LimitExceededException':
          return 'Anmodningen overskred en servicegrænse.';
        case 'UserMigrationFailedException':
          return 'Brugermigrering mislykkedes på grund af forkerte legitimationsoplysninger eller andre årsager.';
        case 'InternalErrorException':
          return 'Der opstod en intern fejl i AWS Cognito.';
        default:
          return 'Ukendt fejl opstod under autentificeringen.';
    }
}


export const findUser = async (email: string) => {
    const filteredUser = await cognito.listUsers({
        UserPoolId: userPoolId,
        AttributesToGet: ['sub', 'email', 'name', 'custom:role'],
        Filter: `email = "${email}"`
    }).promise()

    return filteredUser
}

export const findUserForgetPassword = async (email: string) => {
    const filteredUser = await cognito.listUsers({
        UserPoolId: userPoolId,
        AttributesToGet: ['email'],
        Filter: `email = "${email}"`
    }).promise()

    return filteredUser
}

const zeroPad = (num: number, places: number = 2) => String(num).padStart(places, '0')

const formatDuration = (duration: Duration) => {
    if (duration.hours() > 0) {
        return `${zeroPad(duration.hours())} : ${zeroPad(duration.minutes())} : `
            + `${zeroPad(duration.seconds())}`
    } else if (duration.minutes() > 0) {
        return `00 : ${zeroPad(duration.minutes())} : ${zeroPad(duration.seconds())}`
    } else {
        return `00 : 00 : ${zeroPad(duration.seconds())} `
    }
}

export const formatSeconds = (seconds: number) => {
    return formatDuration(moment.duration(seconds, 'seconds'))
}

export const formatTimeline = (seconds: number) => {
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)

    if (hours > 0) {
        return `${hours}h  ${minutes - hours * 60}m`
    } else {
        return `${minutes}m`
    }

}

export const useHistoryBackOverride = (message: string, onBack?: () => void) => {
    const [$goBack, $setGoBack] = useState(false)
    const navigate = useNavigate()

    const onBackButtonEvent = (e: any) => {
        e.preventDefault()
        if (!$goBack) {
            // eslint-disable-next-line no-alert
            if (window.confirm(message)) {
                if (onBack) {
                    onBack()
                }
                $setGoBack(true)
                navigate('/')
            } else {
                window.history.pushState(null, window.document.title, window.location.pathname)
                $setGoBack(false)
            }
        }
    }

    useEffect(() => {
        window.history.pushState(null, window.document.title, window.location.pathname)
        window.addEventListener('popstate', onBackButtonEvent)
        return () => {
            window.removeEventListener('popstate', onBackButtonEvent)
        }
    })
}

export const makeLocalUri = (pathname: string) => {
    const { protocol, port, hostname } = window.location
    return `${protocol}//${hostname}:${port || ''}${pathname}`
}

export function getFileInfo(file: any, mime: string = ''): { filename: string, mime: string } {
    const pos: number = String(file.name).lastIndexOf('.')

    if (mime === 'image/jpeg') {
        const filename = `${String(file.name)
            .substr(0, pos < 0 ? String(file.name).length : pos)}.jpg`

        return {
            filename,
            mime: 'image/jpeg'
        }
    }

    return {
        filename: file.name,
        mime: file.type,
    }
}



/**
 * Upload the first element from files array to S3
 * @param folderPrefix folder name to place S3
 * @param file
 * @returns objectUUID | undefined
 */
export const uploadS3Handler = async (folderPrefix: string, file: File | null):
    Promise<Record<string, string> | undefined> => {
    if (!file) return

    const extension = file.type
    try {
        //@ts-ignore
        const uploadRes = await Storage.put(file.upload.uuid, file, {
            contentType: extension,
            customPrefix: { public: folderPrefix }
        })

        if (typeof uploadRes.key !== 'string') return

        // eslint-disable-next-line consistent-return
        return { key: uploadRes.key, extension }
    } catch (error) {//@ts-ignore
        throw new Error(error?.response?.data?.message || error.message);
    }
}

/**
 * Extract UUID from a Key(PK, SK)
 * @param key Prefix for PK key
 * @returns 
 */
export const getUUIDFromKey = (key: string): string => {
    if (!key?.includes('#')) return ''

    return key.split('#')[1]
}

export const eraseRichText = (searchString: string): string => {
    if (searchString?.includes('ql-formula')) {
        const stringToHTML = function (str: string) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(str, 'text/html');
            return doc.body;
        };
        const document = stringToHTML(searchString)
        const searchValues: string[] = []
        const objects = document.querySelectorAll('[data-value]')
        objects.forEach(o => searchValues.push(o.getAttribute('data-value')))
        return searchValues.join(',')

    } else {
        return searchString.replace(/<\/?[^>]+(>|$)/g, "")
    }
}

export const generateQrCode = async (message: string, callback: (value: string) => void) => {
    try {
        const response = await QRCode.toDataURL(message)
        callback(response)
    } catch (error) {
        console.log({ error })
    }
}

export const shuffle = (a: any[]) => {
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
}
